import { Vector3 } from 'three';
import { BALANCE } from '../config/balance';
import type { Drone } from '../enemies/Drone';

const scratchToDrone = new Vector3();

export type MeleeOutcome = 'hit' | 'finished' | 'cooldown' | 'no-target';

/**
 * The close-range claw strike, bound to F.
 *
 * The sweep is omnidirectional within arm's reach: anything alive inside the
 * reach takes the blow, whether it is in front of the head or beside the
 * body, because finishing a captured drone should never depend on facing
 * perfectly. A netted drone is destroyed outright, which is what the capture
 * net is for; a live one wears down over a few strikes.
 *
 * The cooldown runs on the body clock, so bullet time slows the claw along
 * with every other weapon.
 */
export class Melee {
  private cooldown = 0;
  private lastOutcomeValue: MeleeOutcome = 'no-target';

  get lastOutcome(): MeleeOutcome {
    return this.lastOutcomeValue;
  }

  get cooldownRemaining(): number {
    return this.cooldown;
  }

  /** 1 just after a strike, 0 when the claw is ready again. */
  get cooldownRatio(): number {
    return this.cooldown > 0 ? this.cooldown / BALANCE.combat.meleeCooldown : 0;
  }

  update(dt: number): void {
    this.cooldown = Math.max(0, this.cooldown - dt);
  }

  reset(): void {
    this.cooldown = 0;
    this.lastOutcomeValue = 'no-target';
  }

  /**
   * Strike from `origin` (the head, so the reach is where the claw is).
   * Returns the drone that was hit, or null; `lastOutcome` explains why.
   */
  tryStrike(origin: Vector3, drones: readonly Drone[]): Drone | null {
    if (this.cooldown > 0) {
      this.lastOutcomeValue = 'cooldown';
      return null;
    }

    let best: Drone | null = null;
    let bestDistance: number = BALANCE.combat.meleeRange;
    for (const drone of drones) {
      if (drone.isDestroyed) continue;
      const distance = scratchToDrone.subVectors(drone.position, origin).length();
      if (distance < bestDistance) {
        best = drone;
        bestDistance = distance;
      }
    }

    if (!best) {
      this.lastOutcomeValue = 'no-target';
      return null;
    }

    this.cooldown = BALANCE.combat.meleeCooldown;
    best.takeDamage(best.isNetted ? BALANCE.combat.droneHealth : BALANCE.combat.meleeDamage);
    // The claw kills outright, so a landed strike reads as a finish whether the
    // drone was netted or not.
    this.lastOutcomeValue = best.isDestroyed ? 'finished' : 'hit';
    return best;
  }
}
